<script setup lang="ts">
/**
 * A loading placeholder shaped like the content it replaces.
 *
 * Shape matters more than the shimmer: a block the size of the real row keeps the
 * layout from jumping when data lands, which is the actual complaint behind
 * "the page feels janky".
 *
 * ── POR QUE TRÊS FORMAS E NÃO UMA ─────────────────────────────────────────
 *
 * Porque uma barra cinza de 36px é a forma de uma linha de tabela e de mais nada.
 * Onde o conteúdo é uma conversa (rosto, nome, prévia) ou um canal (cartão com
 * selo e ações), a mesma barra anuncia um layout que não é o que vai chegar — e a
 * tela pula justamente onde ela é mais olhada, que é a caixa de entrada.
 *
 * Três, e não uma por tela: um esqueleto por componente seria trinta arquivos
 * cinzentos para manter em dia. Estas são as três geometrias que o painel tem.
 */
withDefaults(
	defineProps<{
		rows?: number;
		/** `rows` é a linha de tabela; `list`, a lista com rosto; `cards`, o cartão. */
		shape?: "rows" | "list" | "cards";
		class?: string;
	}>(),
	{ rows: 3, shape: "rows" },
);

/**
 * O esmaecer para baixo — o que está mais longe do topo é o que menos importa
 * enquanto se espera, e é assim que a lista real também é lida.
 */
function fade(row: number): { opacity: number } {
	return { opacity: 1 - row * 0.12 };
}
</script>

<template>
	<div class="space-y-2" :class="$props.class" aria-hidden="true">
		<template v-if="shape === 'list'">
			<div
				v-for="row in rows"
				:key="row"
				class="flex animate-pulse items-center gap-2.5 px-2.5 py-2"
				:style="fade(row)"
			>
				<span class="size-8 shrink-0 rounded-full bg-bg-muted" />
				<span class="min-w-0 flex-1 space-y-1.5">
					<span class="block h-3 w-2/5 rounded bg-bg-muted" />
					<span class="block h-2.5 w-4/5 rounded bg-bg-muted" />
				</span>
			</div>
		</template>

		<template v-else-if="shape === 'cards'">
			<div
				v-for="row in rows"
				:key="row"
				class="animate-pulse rounded-xl border border-border bg-surface p-4"
				:style="fade(row)"
			>
				<div class="flex items-center gap-3">
					<span class="size-10 shrink-0 rounded-lg bg-bg-muted" />
					<span class="min-w-0 flex-1 space-y-1.5">
						<span class="block h-3 w-1/3 rounded bg-bg-muted" />
						<span class="block h-2.5 w-1/2 rounded bg-bg-muted" />
					</span>
					<span class="h-5 w-16 shrink-0 rounded-full bg-bg-muted" />
				</div>
				<div class="mt-3 flex gap-1.5">
					<span class="h-7 w-20 rounded-full bg-bg-muted" />
					<span class="h-7 w-16 rounded-full bg-bg-muted" />
				</div>
			</div>
		</template>

		<template v-else>
			<div
				v-for="row in rows"
				:key="row"
				class="h-9 animate-pulse rounded-lg bg-bg-muted"
				:style="fade(row)"
			/>
		</template>
	</div>
</template>
